import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { UserSettingsService } from '../Account/userSettings.service';
import { WeissmanModalService } from '../Compliance/WeissmanModalService';
import { ProcessingRepository } from '../Core-Repositories';
import { QuickResultType } from '../Search/Quick/Criteria/quickCriteria.model';
import { QuickFiltersService } from '../Search/Quick/Criteria/quickFilters.service';
import { SearchService } from '../Search/search.service';
import { Settings } from '../User/account.service';
import {
    DuplicateIntakeItemModalComponent,
    DuplicateIntakeItemModalParams,
} from './Documents/Modals/duplicateIntakeItemModal.component';
import {
    DocumentProcessingExceptionModalComponent,
    DocumentProcessingExceptionModalParams,
} from './Documents/Modals/documentProcessingExceptionModal.component';

import * as _ from 'lodash';

export interface DocumentResultType {
    name: string;
    type: string;
    id: number;
    sortBy?: string;
}

@Injectable({ providedIn: 'root' })
export class ProcessingService {
    constructor(
        private readonly _userSettingsService: UserSettingsService,
        private readonly _processingRepository: ProcessingRepository,
        private readonly _searchService: SearchService,
        private readonly _quickFiltersService: QuickFiltersService,
        private readonly _weissmanModalService: WeissmanModalService,
    ) {}

    private _changeProcessingDocumentSubject: Subject<void> = new Subject();
    private _changeProcessingDocument$: Observable<void> = this._changeProcessingDocumentSubject.asObservable();

    get changeProcessingDocument$(): Observable<void> {
        return this._changeProcessingDocument$;
    }

    getCriteria(): any {
        return this.getSavedCriteria() || this.getDefaultCriteria();
    }

    getDefaultCriteria(): any {
        return {
            activeFilters: this._quickFiltersService.getDocumentActive(),
            inactiveFilters: this._quickFiltersService.getDocumentInactive(),
            activeOnly: true,
            primaryAcctNumOnly: true,
        };
    }

    getCriteriaWithIdp(ocrResults, idpConfidence, associatedEntityMissing) {
        // Get the defaults and overwrite with saved criteria which is likely missing some new fields
        const savedCriteria = this.getSavedCriteria();
        const criteria = this.getDefaultCriteria();
        if (savedCriteria) {
            criteria.activeFilters = criteria.activeFilters.map((x) => {
                const saved = savedCriteria.activeFilters.find((y) => y.field === x.field);
                return { ...x, ...saved, ocrFields: x.ocrFields };
            });
        }

        const analyticsOcrFields = ['parcelAcctNum', 'taxCollector', 'taxYear'];
        const fieldsCaptured = new Set();

        // Add IDP values to the filters
        criteria['isIdpSearch'] = true;
        criteria.activeFilters.forEach((filter) => {
            if (filter.ocrFields?.length) {
                filter.idpResults = filter.ocrFields
                    .map((field) => this._accessObj(field, ocrResults))
                    .filter((x) => x);

                filter.text = filter.idpResults[0];

                if (_.includes(analyticsOcrFields, filter.field)) {
                    fieldsCaptured.add(filter.name);
                }
            }
        });

        let successfulMatchCount = Number(sessionStorage.getItem('successfulMatchCount')) || 0;

        if (!associatedEntityMissing) {
            successfulMatchCount++;
        }

        sessionStorage.setItem('successfulMatchCount', `${successfulMatchCount}`);

        return criteria;
    }

    getResultTypes(): QuickResultType[] {
        const documentResultTypes: QuickResultType[] = [
            {
                name: 'Assessments',
                type: 'assessment',
                id: 7,
            },
            {
                name: 'Appeals',
                type: 'appeal',
                id: 8,
            },
            {
                name: 'Tax Bills',
                type: 'taxBill',
                id: 9,
            },
            {
                name: 'Refunds',
                type: 'refund',
                id: 14,
            },
            {
                name: 'Comp Filing',
                type: 'filing',
                id: 10,
            },
            {
                name: 'Invoices', // Added for WK-9242  -- Band aid fix. I imagine more result types are missing, but I'm not sure if we need to include all of the entity types
                type: 'invoice',
                id: 21,
            },
        ];

        return _.union(this._searchService.getResultTypes(), documentResultTypes);
    }

    saveCriteria(criteria): Promise<Settings> {
        let criteriaSetting: Settings = {
            id: 0,
            name: 'Document-Processing-Criteria',
            value: criteria,
            groupId: 13,
            groupName: 'Document-Processing-Criteria',
            folderId: 20,
            folderName: 'Document-Processing-Criteria',
        };

        const existingCriteriaSettings = this._userSettingsService.getSettingsByGroup(13);

        if (existingCriteriaSettings.length) {
            criteriaSetting = _.cloneDeep(existingCriteriaSettings[0]);
            criteriaSetting.value = _.cloneDeep(criteria);
        }

        this._userSettingsService.save(criteriaSetting);

        return this._userSettingsService.save(criteriaSetting);
    }

    getSavedCriteria(): any {
        const existingCriteriaSettings = this._userSettingsService.getSettingsByGroup(13);

        if (existingCriteriaSettings.length) {
            const savedCriteria = _.cloneDeep(existingCriteriaSettings[0].value);
            savedCriteria.executeSearch = false;
            return savedCriteria;
        } else {
            return null;
        }
    }

    async search(pageNumber, numberToGet, criteria, resultTypes) {
        const resultTypeObj: any = _.find(resultTypes, {
            type: criteria.resultType,
        });

        const queryParams = {
            resultsAs: resultTypeObj.id,
            activeOnly: criteria.activeOnly,
            primaryAcctNumOnly: criteria.primaryAcctNumOnly,
            pageNumber: pageNumber,
            pageSize: numberToGet,
            anticipatedItemsOnly: criteria.anticipated,
            currentIntakeItemID: criteria.currentIntakeItemID,
            includeIntakeItemData: criteria.includeIntakeItemData,
            anticipatedAttachmentTypeID: criteria.anticipated ? criteria.anticipatedAttachmentTypeID : null,
        };

        const query = _.chain(criteria.activeFilters)
            .filter('text')
            .reduce((result, filter) => {
                result[filter.field] = filter.text;

                return result;
            }, {})
            .assign(queryParams)
            .value();

        const response = await lastValueFrom(this._processingRepository.search(query));

        return {
            results: response,
        };
    }

    processDocumentException(
        documentID,
        taskID,
        entityDetails,
        actionID,
        newAssignment,
        commentBody,
        newDocumentTypeID,
        data,
        intakeItemRowVersion,
        parcelChanges,
    ) {
        return lastValueFrom(
            this._processingRepository.processDocumentException(
                documentID,
                taskID,
                entityDetails,
                actionID,
                newAssignment,
                commentBody,
                newDocumentTypeID,
                data,
                intakeItemRowVersion,
                parcelChanges,
            ),
        );
    }

    processDocumentBulkException(payload) {
        return lastValueFrom(this._processingRepository.processDocumentBulkException(payload));
    }

    completeItem(
        documentID,
        taskID,
        entityDetails,
        newDocumentTypeID,
        data,
        intakeItemRowVersion,
        parcelChanges,
        extraInfoJSON,
    ) {
        return lastValueFrom(
            this._processingRepository.completeItem(
                documentID,
                taskID,
                entityDetails,
                newDocumentTypeID,
                data,
                intakeItemRowVersion,
                parcelChanges,
                extraInfoJSON,
            ),
        );
    }

    saveItemEntity(
        documentID,
        taskID,
        entityDetails,
        newDocumentTypeID,
        data,
        intakeItemRowVersion,
        parcelChanges,
        extraInfoJSON,
    ): Promise<any> {
        return lastValueFrom(
            this._processingRepository.saveItemEntity(
                documentID,
                taskID,
                entityDetails,
                newDocumentTypeID,
                data,
                intakeItemRowVersion,
                parcelChanges,
                extraInfoJSON,
            ),
        );
    }

    getEntityTree(entityTypeId: number, entityId: number, extended: boolean): Promise<Core.EntityTree> {
        return lastValueFrom(this._processingRepository.getEntityTree(entityTypeId, entityId, extended));
    }

    getResultTypesByDocumentType(documentType): QuickResultType[] {
        return _.filter(this.getResultTypes(), (resultType) => {
            const entity = _.capitalize(resultType.type);

            switch (entity) {
                case 'Taxbill':
                    return documentType['allowAtTaxBill'] || documentType.allowAtTaxPayment;
                case 'Filing':
                    return documentType[`allowAt${entity}`] || documentType.allowAtPPReturn;
                default:
                    return documentType[`allowAt${entity}`];
            }
        });
    }

    async launchDocumentProcessingExceptionsModal(modalParams: DocumentProcessingExceptionModalParams): Promise<any> {
        return await this._weissmanModalService.showAsync(
            DocumentProcessingExceptionModalComponent,
            modalParams,
            'modal-md',
        );
    }

    //Launch duplication modal with single item
    async launchDuplicateIntakeItemModal(duplicateItemInfo): Promise<any> {
        const params: DuplicateIntakeItemModalParams = {
            isBulk: false,
            duplicateItem: duplicateItemInfo,
            optionalArgs: null,
        };

        return await this._weissmanModalService.showAsync(DuplicateIntakeItemModalComponent, params, 'modal-md');
    }

    //call API with list
    createDuplicateIntakeItem(duplicateItem): Promise<any> {
        return lastValueFrom(this._processingRepository.createDuplicateIntakeItem(duplicateItem));
    }

    createDuplicateIntakeItemBulk(duplicateItem): Promise<any> {
        return lastValueFrom(this._processingRepository.createDuplicateIntakeItemBulk(duplicateItem));
    }

    //call API with single

    processNoAppealWarranted(payload): Promise<Core.BulkOperationResult[]> {
        return lastValueFrom(this._processingRepository.processNoAppealWarranted(payload));
    }

    processAppealWarranted(payload, isForlitigationReferral?: boolean): Promise<Core.BulkOperationResult[]> {
        return lastValueFrom(this._processingRepository.processAppealWarranted(payload, isForlitigationReferral));
    }

    completeFileAppeal(payload): Promise<any> {
        return lastValueFrom(this._processingRepository.completeFileAppeal(payload));
    }

    getAppealLevels(taskIDs: number[]): Promise<any> {
        return lastValueFrom(this._processingRepository.getAppealLevels(taskIDs));
    }

    changeDueDate(taskIDs: number[], dueDate: Date, searchTimeStamp: Date): Promise<any> {
        return lastValueFrom(this._processingRepository.changeDueDate(taskIDs, dueDate, searchTimeStamp));
    }

    processPaymentReviewed(payload) {
        return lastValueFrom(this._processingRepository.processPaymentReviewed(payload));
    }

    getBulkDocumentExceptionData(taskIds: number[]): Promise<Core.BulkDocumentExceptionData> {
        return lastValueFrom(this._processingRepository.getBulkDocumentExceptionData(taskIds));
    }

    changeProcessingDocument(): void {
        return this._changeProcessingDocumentSubject.next();
    }

    performOCR(taskIds: number[]): Promise<any> {
        return lastValueFrom(this._processingRepository.performOCR(taskIds));
    }

    private _accessObj(path: string, object: any): any {
        return path.split('.').reduce((o, i) => {
            return o ? o[i] : null;
        }, object);
    }
}
